import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, XCircle, ArrowRight, AlertTriangle, ServerCrash, Link2Off, Timer, AlertOctagon, Check } from 'lucide-react';
import { fetchBacklinkStats } from '../api/backlinks';
import { cn } from '../lib/utils';

const statusConfig = {
  live: {
    label: 'Live',
    icon: CheckCircle,
    color: 'success',
    glowClass: 'glow-success',
    bgClass: 'bg-success/10',
    textClass: 'text-success',
    borderClass: 'border-success/30 hover:border-success/50',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    color: 'warning',
    glowClass: 'glow-warning',
    bgClass: 'bg-warning/10',
    textClass: 'text-warning',
    borderClass: 'border-warning/30 hover:border-warning/50',
  },
  redirected: {
    label: 'Redirect',
    icon: ArrowRight,
    color: 'info',
    glowClass: 'glow-info',
    bgClass: 'bg-info/10',
    textClass: 'text-info',
    borderClass: 'border-info/30 hover:border-info/50',
  },
  '404': {
    label: '404',
    icon: XCircle,
    color: 'destructive',
    glowClass: 'glow-destructive',
    bgClass: 'bg-destructive/10',
    textClass: 'text-destructive',
    borderClass: 'border-destructive/30 hover:border-destructive/50',
  },
  '5xx': {
    label: '5xx',
    icon: ServerCrash,
    color: 'destructive',
    glowClass: 'glow-destructive',
    bgClass: 'bg-destructive/10',
    textClass: 'text-destructive',
    borderClass: 'border-destructive/30 hover:border-destructive/50',
  },
  removed: {
    label: 'Removed',
    icon: Link2Off,
    color: 'destructive',
    glowClass: 'glow-destructive',
    bgClass: 'bg-destructive/10',
    textClass: 'text-destructive',
    borderClass: 'border-destructive/30 hover:border-destructive/50',
  },
  nofollow_changed: {
    label: 'Nofollow',
    icon: AlertTriangle,
    color: 'warning',
    glowClass: 'glow-warning',
    bgClass: 'bg-warning/10',
    textClass: 'text-warning',
    borderClass: 'border-warning/30 hover:border-warning/50',
  },
  timeout: {
    label: 'Timeout',
    icon: Timer,
    color: 'muted',
    glowClass: '',
    bgClass: 'bg-muted/50',
    textClass: 'text-muted-foreground',
    borderClass: 'border-border/50 hover:border-border',
  },
  issues: {
    label: 'Issues',
    icon: AlertOctagon,
    color: 'destructive',
    glowClass: 'glow-destructive',
    bgClass: 'bg-destructive/10',
    textClass: 'text-destructive',
    borderClass: 'border-destructive/30 hover:border-destructive/50',
  },
};

function AnimatedCounter({ value, duration = 1 }) {
  return (
    <motion.span
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration }}
      >
        {value}
      </motion.span>
    </motion.span>
  );
}

/**
 * HealthScoreRing - SVG donut chart showing overall backlink health.
 * Score = percentage of live backlinks out of total.
 * Color: green >80, yellow >50, red <=50.
 */
function HealthScoreRing({ stats }) {
  const total = stats.total || 0;
  const live = stats.live || 0;
  const score = total > 0 ? Math.round((live / total) * 100) : 0;

  // Circumference of the ring (2 * PI * r where r=24)
  const circumference = 2 * Math.PI * 24; // ~150.8

  // Determine ring color based on health score
  let ringColor;
  let ringColorClass;
  if (score > 80) {
    ringColor = 'hsl(var(--success))';
    ringColorClass = 'fill-success';
  } else if (score > 50) {
    ringColor = 'hsl(var(--warning))';
    ringColorClass = 'fill-warning';
  } else {
    ringColor = 'hsl(var(--destructive))';
    ringColorClass = 'fill-destructive';
  }

  return (
    <div className="flex flex-col items-center gap-1 flex-shrink-0">
      <svg width="60" height="60" viewBox="0 0 60 60" className="drop-shadow-sm">
        {/* Background track */}
        <circle
          cx="30"
          cy="30"
          r="24"
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="4"
        />
        {/* Filled arc representing health score */}
        <motion.circle
          cx="30"
          cy="30"
          r="24"
          fill="none"
          stroke={ringColor}
          strokeWidth="4"
          strokeDasharray={`${(score / 100) * circumference} ${circumference}`}
          strokeLinecap="round"
          transform="rotate(-90 30 30)"
          initial={{ strokeDasharray: `0 ${circumference}` }}
          animate={{ strokeDasharray: `${(score / 100) * circumference} ${circumference}` }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
        {/* Score number in center */}
        <text
          x="30"
          y="32"
          textAnchor="middle"
          className={cn('text-sm font-bold', ringColorClass)}
          style={{ fill: ringColor }}
        >
          {score}
        </text>
      </svg>
      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
        Health
      </span>
    </div>
  );
}

function StatCard({ status, count, onClick, isActive, isCelebration = false }) {
  const config = statusConfig[status];
  if (!config) return null;

  const Icon = config.icon;

  // "All checked" celebration: when pending count is 0, show a checkmark
  if (isCelebration) {
    return (
      <motion.button
        whileHover={{ scale: 1.04, y: -3 }}
        whileTap={{ scale: 0.97 }}
        transition={{ duration: 0.2 }}
        onClick={onClick}
        className={cn(
          'relative flex flex-col items-center justify-center p-3 rounded-xl border transition-all duration-300 min-w-[90px]',
          'bg-success/5 backdrop-blur-sm',
          'border-success/40 hover:border-success/60',
          isActive && 'glow-success ring-2 ring-success/30 shadow-lg',
          'cursor-pointer group'
        )}
      >
        <div className="relative z-10 flex flex-col items-center gap-1">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 15 }}
          >
            <Check className="h-5 w-5 text-success" />
          </motion.div>
          <span className="text-xs font-semibold text-success">
            All checked
          </span>
        </div>
      </motion.button>
    );
  }

  return (
    <motion.button
      whileHover={{ scale: 1.04, y: -3 }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className={cn(
        'relative flex flex-col items-center justify-center p-3 rounded-xl border transition-all duration-300 min-w-[90px]',
        'bg-card/30 backdrop-blur-sm',
        config.borderClass,
        // Active state: prominent ring + glow + enhanced background
        isActive && cn(
          config.glowClass,
          'ring-2 ring-offset-1 ring-offset-transparent',
          config.color === 'success' && 'ring-success/40',
          config.color === 'warning' && 'ring-warning/40',
          config.color === 'info' && 'ring-info/40',
          config.color === 'destructive' && 'ring-destructive/40',
          config.color === 'muted' && 'ring-border/40',
          config.bgClass,
          'shadow-lg'
        ),
        !isActive && 'opacity-80 hover:opacity-100 hover:shadow-md',
        'cursor-pointer group'
      )}
    >
      {/* Subtle glow overlay */}
      <div
        className={cn(
          'absolute inset-0 rounded-xl transition-opacity duration-300',
          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50',
          config.glowClass
        )}
      />

      <div className="relative z-10 flex flex-col items-center gap-1">
        <Icon className={cn('h-4 w-4 transition-transform duration-200 group-hover:scale-110', config.textClass)} />
        <span className="text-2xl font-bold text-foreground">
          <AnimatedCounter value={count} />
        </span>
        <span className={cn('text-xs font-medium', config.textClass)}>
          {config.label}
        </span>
      </div>
    </motion.button>
  );
}

export default function StatusStatsBar({ projectId, selectedStatuses = [], onStatusClick }) {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['backlink-stats', projectId],
    queryFn: () => fetchBacklinkStats(projectId),
    enabled: !!projectId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="glow-card-filled p-4 mb-6">
        <div className="flex items-center gap-4">
          {/* Health ring skeleton */}
          <div className="w-[60px] h-[60px] rounded-full bg-card/30 border border-border/30 animate-pulse flex-shrink-0" />
          <div className="flex gap-3 overflow-x-auto pb-2 flex-1">
            {['live', 'pending', 'redirected', '404', 'removed'].map((status) => (
              <div
                key={status}
                className="min-w-[90px] h-[88px] rounded-xl bg-card/30 border border-border/30 animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  // Display order with most important statuses first
  const displayOrder = ['live', 'pending', 'issues', 'redirected', '404', '5xx', 'removed', 'nofollow_changed', 'timeout'];

  const handleClick = (status) => {
    if (onStatusClick) {
      // If clicking "issues", select all non-live, non-pending statuses
      if (status === 'issues') {
        const issueStatuses = ['removed', 'redirected', '404', '5xx', 'nofollow_changed', 'timeout'];
        onStatusClick(issueStatuses);
      } else {
        onStatusClick([status]);
      }
    }
  };

  const pendingCount = stats.pending ?? 0;

  return (
    <div className="glow-card-filled p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-muted-foreground">Status Overview</h3>
        <span className="text-xs text-muted-foreground">
          {stats.total} total backlinks
        </span>
      </div>

      <div className="flex items-center gap-4">
        {/* Health Score Ring */}
        <HealthScoreRing stats={stats} />

        {/* Divider */}
        <div className="w-px h-14 bg-border/40 flex-shrink-0" />

        {/* Status cards */}
        <div className="flex gap-3 overflow-x-auto pb-1 flex-1">
          {displayOrder.map((status) => {
            const count = stats[status] ?? 0;

            // Skip statuses with 0 count except for live and pending/issues
            if (count === 0 && !['live', 'pending', 'issues'].includes(status)) {
              return null;
            }

            // Check if this status is currently active in filter
            const isActive = selectedStatuses.length === 0
              ? false
              : status === 'issues'
                ? selectedStatuses.some(s => ['removed', 'redirected', '404', '5xx', 'nofollow_changed', 'timeout'].includes(s))
                : selectedStatuses.includes(status);

            // Show "All checked" celebration when pending is 0
            if (status === 'pending' && pendingCount === 0) {
              return (
                <StatCard
                  key={status}
                  status={status}
                  count={0}
                  onClick={() => handleClick(status)}
                  isActive={isActive}
                  isCelebration
                />
              );
            }

            return (
              <StatCard
                key={status}
                status={status}
                count={count}
                onClick={() => handleClick(status)}
                isActive={isActive}
              />
            );
          })}
        </div>
      </div>

      {/* Quick filter chips */}
      {selectedStatuses.length > 0 && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/30">
          <span className="text-xs text-muted-foreground">Filtering:</span>
          {selectedStatuses.map((status) => (
            <span
              key={status}
              className={cn(
                'text-xs px-2 py-0.5 rounded-full',
                statusConfig[status]?.bgClass,
                statusConfig[status]?.textClass
              )}
            >
              {statusConfig[status]?.label || status}
            </span>
          ))}
          <button
            onClick={() => onStatusClick && onStatusClick([])}
            className="text-xs text-primary hover:text-primary/80 ml-auto"
          >
            Clear filter
          </button>
        </div>
      )}
    </div>
  );
}
